import { Fragment } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
  Typography,
} from '@eye-able/ui';
import type {
  PolicyMatchMode,
  PolicyResponse,
  PolicyRuleGroup,
} from '@http/queries/useCreatePolicy';
import { getRuleFieldLabel, getRuleOperatorLabel } from './PolicyRuleLabels';
import { useInlineTranslation } from '@/i18n/hooks/useInlineTranslation';

function MatchModeConnector({ matchMode }: { matchMode: PolicyMatchMode }) {
  const { t } = useInlineTranslation();
  return (
    <div className='eye:flex eye:items-center eye:gap-xs'>
      <span className='eye:h-px eye:flex-1 eye:bg-grey-20' />
      <Typography variant='label-s' className='eye:text-grey-120'>
        {matchMode === 'all' ? t('and') : t('or')}
      </Typography>
      <span className='eye:h-px eye:flex-1 eye:bg-grey-20' />
    </div>
  );
}

const scopeNounKeys: Record<string, string> = {
  image: 'nounImage',
  'meta-tag': 'nounMetaTag',
  file: 'nounFile',
};

/** "Any image where…" header for scoped groups; page groups need none. */
function RuleGroupBlock({ group }: { group: PolicyRuleGroup }) {
  const { t } = useInlineTranslation();
  const heading =
    group.scope === 'page'
      ? null
      : t(group.quantifier === 'every' ? 'everyScopeWhere' : 'anyScopeWhere', {
          scope: t(scopeNounKeys[group.scope] ?? group.scope),
        });
  return (
    <div className='eye:flex eye:flex-col eye:gap-xs eye:rounded-8 eye:border eye:border-grey-20 eye:p-xs'>
      {heading ? (
        <Typography variant='label-s' className='eye:text-grey-120'>
          {heading}
        </Typography>
      ) : null}
      {group.rules.map((rule, index) => (
        <Fragment key={index}>
          {index > 0 ? (
            <MatchModeConnector matchMode={group.matchMode} />
          ) : null}
          <div className='eye:flex eye:flex-wrap eye:items-baseline eye:gap-4xs'>
            <Typography variant='label-s'>
              {t(getRuleFieldLabel(rule.field))}
            </Typography>
            <Typography className='eye:text-grey-120'>
              {t(getRuleOperatorLabel(rule.operator)).toLowerCase()}
            </Typography>
            <Typography className='eye:break-all'>
              &ldquo;{rule.value}&rdquo;
            </Typography>
          </div>
        </Fragment>
      ))}
    </div>
  );
}

export function PolicyRuleDialog({
  policy,
  onOpenChange,
}: {
  policy: PolicyResponse | null;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useInlineTranslation();
  return (
    <Dialog open={policy !== null} onOpenChange={onOpenChange}>
      <DialogContent maxWidth='md' className='eye:w-full'>
        <DialogTitle>{t('policyRule')}</DialogTitle>
        {policy ? (
          <>
            <DialogDescription>
              {t('conditionsCheckedByPolicy', { policyName: policy.name })}
            </DialogDescription>
            <div className='eye:flex eye:flex-col eye:gap-xs'>
              {policy.rule.groups.map((group, index) => (
                <Fragment key={index}>
                  {index > 0 ? (
                    <MatchModeConnector matchMode={policy.rule.matchMode} />
                  ) : null}
                  <RuleGroupBlock group={group} />
                </Fragment>
              ))}
            </div>
          </>
        ) : null}
        <DialogActions>
          <DialogClose asChild>
            <Button type='button' variant='outline'>
              {t('close')}
            </Button>
          </DialogClose>
        </DialogActions>
      </DialogContent>
    </Dialog>
  );
}

export default PolicyRuleDialog;
